// ============================================================================
//  HR Module — Pointage & Carrière
//  AttendancePage · AttendanceReportPage · EvaluationsPage · TrainingsPage
// ============================================================================

import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  Clock, CheckCircle, XCircle, Users, TrendingUp,
  BarChart3, ChevronLeft, Award, Play, Square,
  Coffee, AlertTriangle, Download, Star, BookOpen, ArrowRight,
  UserCheck, Calendar, Plus, X, Save, Loader2,
} from 'lucide-react'
import { hrApi } from '@/modules/hr/api/hr.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore }         from '@/stores/auth.store'

const fmt     = (n: number) => new Intl.NumberFormat('fr-SN').format(Math.round(n))
const fmtDate = (d: string) => new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' })
const fmtTime = (d: string) => new Date(d).toLocaleTimeString('fr-SN', { hour: '2-digit', minute: '2-digit' })

// ============================================================================
//  ATTENDANCE PAGE — Vue journalière + Clock in/out
// ============================================================================

export default function AttendancePage() {
  const [date, setDate]         = useState(new Date().toISOString().split('T')[0])
  const queryClient             = useQueryClient()
  const addNotification         = useNotificationStore(s => s.addNotification)
  const { user }                = useAuthStore()

  const { data: overview, isLoading } = useQuery({
    queryKey: ['attendanceOverview', date],
    queryFn:  () => hrApi.attendance.getDailyOverview(date),
    refetchInterval: 30000,
  })

  const mutOpts = (msg: string) => ({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['attendanceOverview'] })
      addNotification({ type: 'success', title: 'Pointage', message: msg })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const { mutate: clockIn,  isPending: isClockingIn }  = useMutation({ mutationFn: () => hrApi.attendance.clockIn(), ...mutOpts('Entrée enregistrée.') })
  const { mutate: clockOut, isPending: isClockingOut } = useMutation({ mutationFn: () => hrApi.attendance.clockOut(), ...mutOpts('Sortie enregistrée.') })

  const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string }> = {
    PRESENT:  { label: 'Présent',  color: 'text-[#059669]', dot: 'bg-[#10B981]' },
    LATE:     { label: 'En retard', color: 'text-[#D97706]',  dot: 'bg-[#F59E0B]' },
    HALF_DAY: { label: 'Demi-journée', color: 'text-[var(--blue-d)]', dot: 'bg-[#38BDF8]' },
    ON_LEAVE: { label: 'En congé', color: 'text-[#7C3AED]',  dot: 'bg-[#8B5CF6]' },
    ABSENT:   { label: 'Absent',   color: 'text-[var(--tx3)]',   dot: 'bg-[#D6D2EC]' },
  }

  const myRecord = overview?.employees.find(e => e.id === user?.employeeId)
  const hasClockedIn  = !!myRecord?.attendance?.clockIn
  const hasClockedOut = !!myRecord?.attendance?.clockOut

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Pointage</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Présence de l'équipe</p>
        </div>
        <div className="flex items-center gap-3">
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="px-3 py-2 bg-white border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] focus:outline-none" />
          <Link to="/hr/attendance/report"
            className="flex items-center gap-2 px-4 py-2 bg-white border border-[var(--bd)] hover:bg-[#F0F4FF] text-sm font-medium text-[var(--tx2)] rounded-lg transition-colors shadow-sm">
            <BarChart3 className="w-4 h-4" />
            Rapport
          </Link>
          <button
            onClick={async () => {
              const now = new Date()
              try {
                const data = await hrApi.getMonthlyAttendanceExport(now.getFullYear(), now.getMonth() + 1)
                if (!data?.employees?.length) return
                const headers = ['Matricule', 'Nom', 'Prénom', 'Jours présent', 'Jours absent', 'Retards', 'Heures totales', 'Heures sup']
                const rows = data.employees.map((e: any) => [e.matricule, e.nom, e.prenom, e.daysPresent, e.daysAbsent, e.daysLate, e.totalHours, e.overtimeHours])
                const csv = [headers.join(';'), ...rows.map((r: any[]) => r.join(';'))].join('\n')
                const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
                const link = document.createElement('a')
                link.href = URL.createObjectURL(blob)
                link.download = `pointage_${data.period?.label?.replace('/', '-')}.csv`
                link.click()
              } catch {}
            }}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-sm font-medium text-white rounded-lg transition-colors shadow-sm"
          >
            <Download className="w-4 h-4" />
            Export mois
          </button>
        </div>
      </div>

      {/* Mon pointage */}
      {user?.employeeId && (
        <div className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[var(--tx3)] uppercase tracking-wider mb-4">Mon pointage aujourd'hui</h3>
          <div className="flex items-center gap-4">
            <button
              onClick={() => clockIn()}
              disabled={hasClockedIn || isClockingIn}
              className="flex items-center gap-2 px-5 py-3 bg-emerald-600 hover:bg-[#10B981] text-[var(--brand-dark)] rounded-xl text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Play className="w-4 h-4" />
              {isClockingIn ? 'Pointage…' : hasClockedIn ? `Entré à ${fmtTime(myRecord!.attendance!.clockIn!)}` : 'Pointer l\'entrée'}
            </button>
            <button
              onClick={() => clockOut()}
              disabled={!hasClockedIn || hasClockedOut || isClockingOut}
              className="flex items-center gap-2 px-5 py-3 bg-[var(--surface-2)] hover:bg-[var(--bd)] text-[var(--brand-dark)] rounded-xl text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Square className="w-4 h-4" />
              {isClockingOut ? 'Pointage…' : hasClockedOut ? `Sorti à ${fmtTime(myRecord!.attendance!.clockOut!)}` : 'Pointer la sortie'}
            </button>
            {myRecord?.attendance?.totalMinutes && (
              <div className="ml-4 text-center">
                <p className="text-2xl font-bold text-[var(--brand-dark)]">
                  {Math.floor(myRecord.attendance.totalMinutes / 60)}h{String(myRecord.attendance.totalMinutes % 60).padStart(2, '0')}
                </p>
                <p className="text-xs text-[var(--tx3)]">Temps travaillé</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats du jour */}
      {overview?.summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: 'Total',     value: overview.summary.total,   color: 'text-[var(--brand-dark)]' },
            { label: 'Présents',  value: overview.summary.present, color: 'text-[#059669]' },
            { label: 'En congé',  value: overview.summary.onLeave, color: 'text-[#7C3AED]' },
            { label: 'Absents',   value: overview.summary.absent,  color: 'text-[var(--tx3)]' },
            { label: 'En retard', value: overview.summary.late,    color: 'text-[#D97706]' },
          ].map(k => (
            <div key={k.label} className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-4 text-center">
              <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
              <p className="text-xs text-[var(--tx3)] mt-0.5">{k.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table présence */}
      <div className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2"><div className="w-6 h-6 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--bd)]">
                {['Employé', 'Entrée', 'Sortie', 'Temps travaillé', 'Heures sup', 'Statut'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--bd)]">
              {(overview?.employees ?? []).map(emp => {
                const cfg = STATUS_CONFIG[emp.status] ?? STATUS_CONFIG.ABSENT
                return (
                  <tr key={emp.id} className="hover:bg-[var(--surface-2)] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-[var(--brand-dark)] text-xs font-bold">
                          {emp.firstName[0]}{emp.lastName[0]}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-[var(--brand-dark)]">{emp.firstName} {emp.lastName}</p>
                          <p className="text-xs text-[var(--tx3)]">{emp.matricule}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--tx2)]">
                      {emp.attendance?.clockIn ? fmtTime(emp.attendance.clockIn) : '—'}
                      {emp.attendance?.lateMinutes && emp.attendance.lateMinutes > 0 && (
                        <span className="ml-1 text-xs text-[#D97706]">+{emp.attendance.lateMinutes}min</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--tx2)]">
                      {emp.attendance?.clockOut ? fmtTime(emp.attendance.clockOut) : emp.attendance?.clockIn ? <span className="text-[#D97706] text-xs">En cours</span> : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--brand-dark)]">
                      {emp.attendance?.totalMinutes
                        ? `${Math.floor(emp.attendance.totalMinutes / 60)}h${String(emp.attendance.totalMinutes % 60).padStart(2, '0')}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {(emp.attendance?.overtimeMinutes ?? 0) > 0
                        ? <span className="text-[var(--blue-d)]">+{Math.floor(emp.attendance!.overtimeMinutes! / 60)}h{String(emp.attendance!.overtimeMinutes! % 60).padStart(2, '0')}</span>
                        : <span className="text-[var(--tx3)]">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {emp.leave ? emp.leave.leaveType?.name ?? 'En congé' : cfg.label}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  EVALUATIONS PAGE
// ============================================================================

export function EvaluationsPage() {
  const [tab, setTab] = useState<'campaigns' | 'my-evals'>('campaigns')
  const queryClient   = useQueryClient()
  const addNotification = useNotificationStore(s => s.addNotification)

  const { data: campaignsData } = useQuery({
    queryKey: ['campaigns'],
    queryFn:  () => hrApi.career.getCampaigns(),
  })

  const { mutate: activate } = useMutation({
    mutationFn: (id: string) => hrApi.career.activateCampaign(id),
    onSuccess:  () => { queryClient.invalidateQueries({ queryKey: ['campaigns'] }); addNotification({ type: 'success', title: 'Campagne activée' }) },
    onError:    (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const { mutate: initEvals } = useMutation({
    mutationFn: (id: string) => hrApi.career.initializeEvaluations(id),
    onSuccess:  (data) => { queryClient.invalidateQueries({ queryKey: ['campaigns'] }); addNotification({ type: 'success', title: 'Évaluations initialisées', message: `${data.created} créées.` }) },
    onError:    (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const CAMPAIGN_TYPE: Record<string, string> = {
    ANNUAL: 'Annuelle', SEMI_ANNUAL: 'Semestrielle', TRIAL_PERIOD: 'Période d\'essai',
    QUARTERLY: 'Trimestrielle', PROJECT_BASED: 'Projet',
  }

  const STATUS_BADGE: Record<string, { label: string; color: string }> = {
    DRAFT:     { label: 'Brouillon', color: 'text-[var(--tx3)]' },
    ACTIVE:    { label: 'Active',    color: 'text-[#059669]' },
    COMPLETED: { label: 'Terminée', color: 'text-[var(--blue-d)]' },
    CANCELLED: { label: 'Annulée',  color: 'text-[#DC2626]' },
  }

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Évaluations</h1>
        <Link to="/career/campaigns/new"
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-sm text-white font-medium rounded-lg transition-colors">
          <Award className="w-4 h-4" />
          Nouvelle campagne
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 nx-stagger">
        {[
          { label: 'Campagnes actives', value: campaignsData?.campaigns?.filter(c => c.status === 'ACTIVE').length ?? 0, color: 'text-[#059669] bg-[#10B981]/10', icon: Play },
          { label: 'Total campagnes',   value: campaignsData?.total ?? 0, color: 'text-[var(--blue-d)] bg-[#E0F2FE]', icon: Award },
          { label: 'En cours d\'éval.', value: campaignsData?.campaigns?.reduce((a, c) => a + (c._count?.evaluations ?? 0), 0) ?? 0, color: 'text-[#D97706] bg-[#FEF3C7]', icon: Star },
          { label: 'Cette année',       value: campaignsData?.campaigns?.filter(c => new Date(c.startDate).getFullYear() === new Date().getFullYear()).length ?? 0, color: 'text-[#7C3AED] bg-[#EDE9FE]', icon: Calendar },
        ].map(k => (
          <div key={k.label} className="bg-white border border-[var(--bd)] rounded-xl p-5 flex items-center gap-4 shadow-sm hover:shadow-md transition-shadow" style={{borderTop:`3px solid ${k.strip??k.color?.match(/#[0-9A-Fa-f]+/)?.[0]??'var(--brand-orange)'}`}}>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${k.color}`}>
              <k.icon className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-[var(--brand-dark)] leading-none">{k.value}</p>
              <p className="text-xs text-[var(--tx3)]">{k.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Liste campagnes */}
      <div className="space-y-3">
        {(campaignsData?.campaigns ?? []).map(c => {
          const status = STATUS_BADGE[c.status]
          return (
            <div key={c.id} className="bg-white border border-[var(--bd)] rounded-xl p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-[var(--tx3)] mb-1">{c.name}</h3>
                    <span className={`text-xs font-medium ${status?.color}`}>{status?.label}</span>
                    <span className="text-xs text-[var(--tx3)] bg-[#F4F2FB] px-2 py-0.5 rounded-full">{CAMPAIGN_TYPE[c.type]}</span>
                  </div>
                  <p className="text-sm text-[var(--tx3)]">
                    {fmtDate(c.startDate)} → {fmtDate(c.endDate)} · Échéance : {fmtDate(c.dueDate)}
                  </p>
                  <p className="text-xs text-[var(--tx3)] mt-1">{c._count?.evaluations ?? 0} évaluation(s)</p>
                </div>
                <div className="flex items-center gap-2">
                  {c.status === 'DRAFT' && (
                    <button onClick={() => activate(c.id)}
                      className="px-3 py-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 text-[#059669] text-xs font-medium rounded-lg transition-colors">
                      Activer
                    </button>
                  )}
                  {c.status === 'ACTIVE' && (
                    <button onClick={() => initEvals(c.id)}
                      className="px-3 py-1.5 bg-[var(--blue)]/20 hover:bg-[var(--blue)]/30 text-[var(--blue-d)] text-xs font-medium rounded-lg transition-colors">
                      Initialiser évals
                    </button>
                  )}
                  <Link to={`/career/campaigns/${c.id}`}
                    className="flex items-center gap-1 text-xs text-[var(--tx3)] hover:text-[var(--brand-dark)] transition-colors">
                    Voir <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          )
        })}
        {!campaignsData?.campaigns?.length && (
          <div className="flex flex-col items-center justify-center h-48 bg-[var(--bg)] border border-[var(--bd)] rounded-xl text-[var(--tx3)]">
            <Award className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-sm">Aucune campagne d'évaluation</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  TRAININGS PAGE
// ============================================================================

export function TrainingsPage() {
  const [tab, setTab] = useState<'catalog' | 'ongoing'>('catalog')
  const [assignModal, setAssignModal] = useState<{ trainingId: string; trainingName: string } | null>(null)
  const queryClient = useQueryClient()
  const { addNotification } = useNotificationStore(s => s)

  // Formulaire d'assignation
  const [assignForm, setAssignForm] = useState({
    employeeId: '',
    startDate:  '',
    endDate:    '',
    notes:      '',
  })

  const { data: employees } = useQuery({
    queryKey: ['employees-list-assign'],
    queryFn: () => hrApi.employees.list({ status: 'ACTIVE', limit: 200 }),
  })

  const { mutate: doAssign, isPending: isAssigning } = useMutation({
    mutationFn: () => hrApi.career.assignTraining({
      trainingId: assignModal?.trainingId,
      employeeId: assignForm.employeeId,
      startDate:  assignForm.startDate,
      endDate:    assignForm.endDate || undefined,
      notes:      assignForm.notes   || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingCatalog'] })
      addNotification({ type: 'success', title: 'Formation assignée', message: assignModal?.trainingName + ' → ' + (employees?.employees?.find((e: any) => e.id === assignForm.employeeId)?.firstName ?? '') })
      setAssignModal(null)
      setAssignForm({ employeeId: '', startDate: '', endDate: '', notes: '' })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const { data: catalog } = useQuery({
    queryKey: ['trainingCatalog'],
    queryFn:  () => hrApi.career.getTrainingCatalog(),
  })

  const TYPE_COLOR: Record<string, string> = {
    TECHNICAL:     'bg-[#E0F2FE] text-[var(--blue-d)]',
    MANAGEMENT:    'bg-[#EDE9FE] text-[#7C3AED]',
    COMPLIANCE:    'bg-[#FEF3C7] text-[#D97706]',
    SOFT_SKILLS:   'bg-[#10B981]/10 text-[#059669]',
    LANGUAGE:      'bg-[#FDF2F8] text-[#DB2777]',
  }

  return (
    <div className="space-y-5 nx-fade-up">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Formations</h1>
        <Link to="/career/trainings/new"
          className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-sm text-white font-medium rounded-lg transition-colors">
          <Plus className="w-4 h-4" />
          Ajouter une formation
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--bd)]">
        {[
          { key: 'catalog', label: 'Catalogue' },
          { key: 'ongoing', label: 'En cours' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? 'border-blue-500 text-[var(--blue-d)]' : 'border-transparent text-[var(--tx3)] hover:text-[var(--brand-dark)]'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'catalog' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {(catalog ?? []).map(t => (
            <div key={t.id} className="bg-[var(--bg)] border border-[var(--bd)] rounded-xl p-5 hover:border-[rgba(245,124,0,0.25)] transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLOR[t.type] ?? 'bg-[#D6D2EC]/10 text-[var(--tx3)]'}`}>
                  {t.type}
                </div>
                {t.cost && <span className="text-xs text-[var(--tx3)]">{fmt(Number(t.cost))} XOF</span>}
              </div>
              <h3 className="text-sm font-bold text-[var(--brand-dark)] mb-1">{t.name}</h3>
              {t.provider && <p className="text-xs text-[var(--tx3)] mb-2">{t.provider}</p>}
              {t.description && <p className="text-xs text-[var(--tx3)] line-clamp-2">{t.description}</p>}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--bd)]">
                {t.duration && <span className="text-xs text-[var(--tx3)]">{t.duration}h</span>}
                <button
                  onClick={() => setAssignModal({ trainingId: t.id, trainingName: t.name })}
                  className="text-xs text-[var(--blue-d)] hover:underline transition-colors font-medium">
                  Assigner →
                </button>
              </div>
            </div>
          ))}
          {!catalog?.length && (
            <div className="col-span-3 flex flex-col items-center justify-center h-48 bg-[var(--bg)] border border-[var(--bd)] rounded-xl text-[var(--tx3)]">
              <BookOpen className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm">Aucune formation dans le catalogue</p>
            </div>
          )}
        </div>
      )}
      {/* Modal assignation formation */}
      {assignModal && (
        <div className="fixed inset-0 bg-[rgba(22,12,38,0.5)] backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white border border-[var(--bd)] rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-semibold text-[var(--tx)]">Assigner une formation</h3>
                <p className="text-xs text-[var(--tx3)] mt-0.5">{assignModal.trainingName}</p>
              </div>
              <button onClick={() => setAssignModal(null)} className="text-[var(--tx3)] hover:text-[var(--tx)]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Employé *</label>
                <select
                  value={assignForm.employeeId}
                  onChange={e => setAssignForm(f => ({ ...f, employeeId: e.target.value }))}
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
                >
                  <option value="">— Sélectionner un employé —</option>
                  {(employees?.employees ?? []).map((emp: any) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName} ({emp.matricule})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Date début *</label>
                  <input type="date" value={assignForm.startDate}
                    onChange={e => setAssignForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Date fin</label>
                  <input type="date" value={assignForm.endDate}
                    onChange={e => setAssignForm(f => ({ ...f, endDate: e.target.value }))}
                    className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Notes</label>
                <input value={assignForm.notes}
                  onChange={e => setAssignForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Objectifs spécifiques, priorité…"
                  className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setAssignModal(null)}
                className="flex-1 py-2 bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm hover:bg-[var(--surface-3)] transition-colors">
                Annuler
              </button>
              <button
                onClick={() => doAssign()}
                disabled={!assignForm.employeeId || !assignForm.startDate || isAssigning}
                className="flex-1 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors">
                {isAssigning ? 'Assignation…' : 'Assigner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── AttendanceReportPage ─────────────────────────────────────────────────────
export function AttendanceReportPage() {
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-[var(--brand-dark)] mb-4">Rapport de Pointage</h1>
      <p className="text-[var(--tx3)]">Rapport détaillé — en cours d'implémentation.</p>
    </div>
  )
}

// ============================================================================
//  CREATE CAMPAIGN PAGE
// ============================================================================

export function CreateCampaignPage() {
  const navigate = useNavigate()
  const { addNotification } = useNotificationStore(s => s)
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    name:        '',
    type:        'ANNUAL',
    startDate:   '',
    endDate:     '',
    dueDate:     '',
    description: '',
  })

  const { mutate, isPending } = useMutation({
    mutationFn: () => hrApi.career.createCampaign(form),
    onSuccess: (campaign: any) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      addNotification({ type: 'success', title: 'Campagne créée', message: campaign.name + ' — brouillon' })
      navigate('/career/evaluations')
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const TYPES = [
    { value: 'ANNUAL',       label: 'Annuelle' },
    { value: 'SEMI_ANNUAL',  label: 'Semestrielle' },
    { value: 'QUARTERLY',    label: 'Trimestrielle' },
    { value: 'TRIAL_PERIOD', label: "Période d'essai" },
    { value: 'PROJECT_BASED',label: 'Basée projet' },
  ]

  const isValid = form.name && form.startDate && form.endDate && form.dueDate

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)}
          className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
          <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-[var(--tx)]">Nouvelle campagne d'évaluation</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Crée une campagne en brouillon — à activer ensuite</p>
        </div>
      </div>

      <div className="nx-card p-6 space-y-5">
        {/* Nom */}
        <div>
          <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">
            Nom de la campagne *
          </label>
          <input
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Ex: Évaluation annuelle 2026"
            className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
          />
        </div>

        {/* Type */}
        <div>
          <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">
            Type d'évaluation *
          </label>
          <select
            value={form.type}
            onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
            className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
          >
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">
              Début *
            </label>
            <input type="date" value={form.startDate}
              onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
              className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">
              Fin *
            </label>
            <input type="date" value={form.endDate}
              onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))}
              className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">
              Échéance retour *
            </label>
            <input type="date" value={form.dueDate}
              onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
              className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">
            Description (optionnel)
          </label>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Objectifs de la campagne, critères d'évaluation…"
            rows={3}
            className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] resize-none focus:outline-none focus:border-[var(--blue)]"
          />
        </div>

        {/* Info workflow */}
        <div className="bg-[var(--blue-bg)] border border-[rgba(245,124,0,0.2)] rounded-lg p-4">
          <p className="text-xs font-semibold text-[var(--blue-d)] mb-1">Workflow après création :</p>
          <p className="text-xs text-[var(--blue-d)] opacity-80">
            Créer (BROUILLON) → Activer → Initialiser les évaluations pour chaque employé
            → Auto-évaluation → Évaluation manager → Entretien → Clôture
          </p>
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <button onClick={() => navigate(-1)}
          className="px-4 py-2 bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm hover:bg-[var(--surface-3)] transition-colors">
          Annuler
        </button>
        <button onClick={() => mutate()} disabled={!isValid || isPending}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors">
          {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isPending ? 'Création…' : 'Créer la campagne'}
        </button>
      </div>
    </div>
  )
}

// ============================================================================
//  CAMPAIGN DETAIL PAGE
// ============================================================================

export function CampaignDetailPage() {
  const { id }  = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { addNotification } = useNotificationStore(s => s)

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', id],
    queryFn:  () => hrApi.career.getCampaign(id!),
    enabled:  !!id,
  })

  const { mutate: activate, isPending: isActivating } = useMutation({
    mutationFn: () => hrApi.career.activateCampaign(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] })
      addNotification({ type: 'success', title: 'Campagne activée' })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const { mutate: initEvals, isPending: isIniting } = useMutation({
    mutationFn: () => hrApi.career.initializeEvaluations(id!),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] })
      addNotification({ type: 'success', title: 'Évaluations initialisées', message: data.created + ' créées, ' + data.skipped + ' ignorées' })
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const STATUS_BADGE: Record<string, { label: string; color: string }> = {
    DRAFT:     { label: 'Brouillon',  color: 'bg-[var(--surface-2)] text-[var(--tx3)]' },
    ACTIVE:    { label: 'Active',     color: 'bg-[var(--green-bg)] text-[var(--green-text)]' },
    COMPLETED: { label: 'Terminée',   color: 'bg-[var(--blue-bg)] text-[var(--blue-d)]' },
    CANCELLED: { label: 'Annulée',    color: 'bg-[var(--red-bg)] text-[var(--red-text)]' },
  }

  const EVAL_STATUS: Record<string, { label: string; color: string }> = {
    SELF_EVAL_PENDING:    { label: 'Auto-éval. en attente', color: 'text-[var(--amber-text)]' },
    SELF_EVAL_DONE:       { label: 'Auto-éval. faite',      color: 'text-[var(--blue-d)]' },
    MANAGER_EVAL_PENDING: { label: 'Éval. manager att.',    color: 'text-[var(--amber-text)]' },
    MANAGER_EVAL_DONE:    { label: 'Éval. manager faite',   color: 'text-[var(--blue-d)]' },
    REVIEW_MEETING_PENDING:{ label: 'Entretien planifié',   color: 'text-[var(--mauve-d)]' },
    COMPLETED:            { label: 'Terminée',              color: 'text-[var(--green-text)]' },
    SIGNED:               { label: 'Signée',                color: 'text-[var(--green-text)]' },
  }

  const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[var(--blue)]" /></div>
  if (!campaign) return <div className="text-[var(--tx3)] p-6">Campagne introuvable.</div>

  const statusCfg = STATUS_BADGE[(campaign as any).status] ?? STATUS_BADGE['DRAFT']

  return (
    <div className="space-y-5 nx-fade-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)}
            className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
            <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-[var(--tx)]">{(campaign as any).name}</h1>
              <span className={"text-xs px-2 py-0.5 rounded-md font-medium " + statusCfg.color}>
                {statusCfg.label}
              </span>
            </div>
            <p className="text-sm text-[var(--tx3)] mt-0.5">
              {fmtDate((campaign as any).startDate)} → {fmtDate((campaign as any).endDate)}
              &nbsp;·&nbsp;Échéance : {fmtDate((campaign as any).dueDate)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(campaign as any).status === 'DRAFT' && (
            <button onClick={() => activate()} disabled={isActivating}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--green-bg)] text-[var(--green-text)] border border-[rgba(16,185,129,0.25)] rounded-lg text-sm font-medium hover:opacity-80 transition-opacity disabled:opacity-50">
              <Play className="w-4 h-4" />
              {isActivating ? 'Activation…' : 'Activer'}
            </button>
          )}
          {(campaign as any).status === 'ACTIVE' && (
            <button onClick={() => initEvals()} disabled={isIniting}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--blue)] text-white rounded-lg text-sm font-semibold hover:bg-[var(--blue-d)] transition-colors disabled:opacity-50">
              {isIniting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
              {isIniting ? 'Initialisation…' : 'Initialiser évaluations'}
            </button>
          )}
        </div>
      </div>

      {/* Description */}
      {(campaign as any).description && (
        <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl p-4 text-sm text-[var(--tx2)]">
          {(campaign as any).description}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total évaluations', value: (campaign as any).evaluations?.length ?? 0, color: 'text-[var(--blue-d)]' },
          { label: 'Auto-éval. faites', value: (campaign as any).evaluations?.filter((e: any) => e.status !== 'SELF_EVAL_PENDING').length ?? 0, color: 'text-[var(--green-text)]' },
          { label: 'Éval. manager', value: (campaign as any).evaluations?.filter((e: any) => ['MANAGER_EVAL_DONE','REVIEW_MEETING_PENDING','COMPLETED','SIGNED'].includes(e.status)).length ?? 0, color: 'text-[var(--mauve)]' },
          { label: 'Terminées', value: (campaign as any).evaluations?.filter((e: any) => ['COMPLETED','SIGNED'].includes(e.status)).length ?? 0, color: 'text-[var(--green-text)]' },
        ].map(k => (
          <div key={k.label} className="nx-card p-4 text-center">
            <p className={"text-2xl font-bold " + k.color}>{k.value}</p>
            <p className="text-xs text-[var(--tx3)] mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      {/* Liste évaluations */}
      <div className="nx-card overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--bd)] flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--tx)]">
            Évaluations ({(campaign as any).evaluations?.length ?? 0})
          </h2>
        </div>
        {!(campaign as any).evaluations?.length ? (
          <div className="flex flex-col items-center justify-center py-12 text-[var(--tx3)]">
            <Users className="w-8 h-8 mb-2 opacity-30" />
            <p className="text-sm">Aucune évaluation — cliquez "Initialiser évaluations"</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--bd)] bg-[var(--surface-2)]">
                {['Employé', 'Matricule', 'Poste', 'Statut'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {((campaign as any).evaluations ?? []).map((ev: any) => {
                const evStatus = EVAL_STATUS[ev.status] ?? { label: ev.status, color: 'text-[var(--tx3)]' }
                return (
                  <tr key={ev.id} className="border-b border-[var(--bd)] hover:bg-[var(--surface-2)] transition-colors">
                    <td className="px-4 py-3 font-medium text-[var(--tx)]">
                      {ev.employee?.firstName} {ev.employee?.lastName}
                    </td>
                    <td className="px-4 py-3 text-[var(--tx3)] font-mono text-xs">{ev.employee?.matricule}</td>
                    <td className="px-4 py-3 text-[var(--tx2)]">{ev.employee?.jobTitle}</td>
                    <td className="px-4 py-3">
                      <span className={"text-xs font-medium " + evStatus.color}>{evStatus.label}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ============================================================================
//  CREATE TRAINING PAGE
// ============================================================================

export function CreateTrainingPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { addNotification } = useNotificationStore(s => s)

  const [form, setForm] = useState({
    name:        '',
    type:        'TECHNICAL',
    description: '',
    provider:    '',
    duration:    '',
    cost:        '',
  })

  const { mutate, isPending } = useMutation({
    mutationFn: () => hrApi.career.createTraining({
      ...form,
      duration: form.duration ? Number(form.duration) : undefined,
      cost:     form.cost     ? Number(form.cost)     : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainingCatalog'] })
      addNotification({ type: 'success', title: 'Formation ajoutée', message: form.name + ' ajoutée au catalogue' })
      navigate('/career/trainings')
    },
    onError: (err: any) => addNotification({ type: 'error', title: 'Erreur', message: err.response?.data?.message }),
  })

  const TYPES = [
    { value: 'TECHNICAL',   label: 'Technique' },
    { value: 'MANAGEMENT',  label: 'Management' },
    { value: 'COMPLIANCE',  label: 'Conformité / Réglementaire' },
    { value: 'SOFT_SKILLS', label: 'Soft skills' },
    { value: 'LANGUAGE',    label: 'Langue' },
  ]

  const isValid = form.name && form.type

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)}
          className="p-2 hover:bg-[var(--surface-2)] rounded-lg transition-colors">
          <ChevronLeft className="w-5 h-5 text-[var(--tx3)]" />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-[var(--tx)]">Ajouter une formation</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">Ajoute une formation au catalogue RH</p>
        </div>
      </div>

      <div className="nx-card p-6 space-y-5">
        <div>
          <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Nom *</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Ex: SYSCOHADA 2018 — Comptabilité générale"
            className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Type *</label>
          <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
            className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]">
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Organisme / Prestataire</label>
          <input value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
            placeholder="Ex: Cabinet DELOITTE, CESAG, en interne…"
            className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Durée (heures)</label>
            <input type="number" value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))}
              placeholder="Ex: 16"
              className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Coût (XOF)</label>
            <input type="number" value={form.cost} onChange={e => setForm(f => ({ ...f, cost: e.target.value }))}
              placeholder="Ex: 150000"
              className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:border-[var(--blue)]"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-[var(--tx3)] uppercase tracking-wider mb-1.5">Description</label>
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder="Objectifs pédagogiques, contenu, prérequis…"
            rows={3}
            className="w-full px-3 py-2.5 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] resize-none focus:outline-none focus:border-[var(--blue)]"
          />
        </div>
      </div>

      <div className="flex gap-3 justify-end">
        <button onClick={() => navigate(-1)}
          className="px-4 py-2 bg-[var(--surface-2)] text-[var(--tx2)] rounded-lg text-sm hover:bg-[var(--surface-3)] transition-colors">
          Annuler
        </button>
        <button onClick={() => mutate()} disabled={!isValid || isPending}
          className="flex items-center gap-2 px-5 py-2 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-semibold disabled:opacity-50 transition-colors">
          {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isPending ? 'Ajout…' : 'Ajouter au catalogue'}
        </button>
      </div>
    </div>
  )
}
